import {OrderedMap} from './ordered-map';
import {WizardStep} from '../interfaces/wizard-step';
import { Subject, Observable, Subscriber, Subscription, of, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { pdsLogger } from '../pds-logger';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('wizard'), 'wizard.ts');

export class Wizard {
  id: string;

  //One WizardStep object for each item that shows up in the steps widget.
  steps: OrderedMap<WizardStep> = new OrderedMap<WizardStep>(); //Key is the WizardStep.id

  //We can fire this with the WizardSteps that have been mutated.
  changeStream: Subject<Array<WizardStep>> = new Subject<Array<WizardStep>>();

  activeStep: WizardStep | undefined;

  isSequential = true;

  // A callback that can prevent the steps from changing
  // based on whatever the outside programmer wants.  (Validation
  // of the form is the big one here)
  stepChangePrerequisite?: (activeStep: WizardStep, nextStep: WizardStep) => boolean | Observable<boolean>;

  /**
   * When a step gets changed, the wizard changes several properties in the wizardSteps.
   * However, if you need to change some of those properties before they get changeStream fires
   * this interceptor is your chance to do it.
   */
  stepChangeInterceptor?: (stepsChanged: Array<WizardStep>) => void;

  constructor(id: string) {
    this.id = id;
    log('Created new Wizard with id: ' + this.id);
  }

  setSteps(steps: Array<WizardStep>, isSequential?: boolean): void {
    log('setSteps() called');
    if (isSequential != null) {
      this.isSequential = isSequential;
    }

    if (steps instanceof Array && steps.length > 0) {
      delete this.activeStep;
      this.steps.clear();

      for (let step of steps) {
        //The widget needs to have all of the properties filled in so we will initialize them all here.
        step.isComplete = step.isComplete === true;
        step.isActive = step.isActive === true;
        step.isDisabled = step.isDisabled === true;

        this.steps.put(step.id, step);

        if (step.isActive) {
          this.activeStep = step;
        } else if (this.isSequential && this.activeStep != null) {
          step.isDisabled = true;
        }

      }

      if (this.activeStep == null) {
        this.activeStep = steps[0];
        this.activeStep.isActive = true;
        if (this.isSequential) {
          for (let i = 1; i < steps.length; i++) {
            steps[i].isDisabled = true;
          }
        }
      }

      this.fireChangeStream(this.steps.values());
    }
  }

  /**
   * This will make the widget update its contents for the steps in the array.
   * @param changedValues - WizardStep objects that have had their properties mutated.
   */
  fireChangeStream(changedValues: Array<WizardStep>): void {
    log('fireChangeStream() called');
    if (this.stepChangeInterceptor != null) {
      this.stepChangeInterceptor(changedValues);
    }
    this.changeStream.next(changedValues);
  }

  setIsSequential(value: boolean): void {
    log(`setIsSequential(${value}) called`);
    this.isSequential = value;
    if (this.isSequential) {
      let active: WizardStep | undefined;
      this.steps.forEach((step: WizardStep, key: string, index: number) => {
        if (active == null) {
          //everything before and including the active is enabled.
          step.isDisabled = false;
          step.isComplete = true; //If it's the active step we unset this later.
        } else {
          //everything after the active is disabled
          step.isDisabled = true;
        }

        if (step === this.activeStep) {
          active = step;
          step.isComplete = false;
        }
      });
    } else {
      this.steps.forEach((step: WizardStep, key: string, index: number) => {
        step.isDisabled = false;
      });
    }

    this.fireChangeStream(this.steps.values());
  }

  setActiveStep(step: string | WizardStep | undefined): Observable<boolean> {
    log('setActiveStep() called');

    if (typeof step === 'string') {
      let stepId: string = <string>step;
      step = this.steps.get(stepId);
      if (step == null) {
        throw 'Step id: "' + stepId + '" does not exist in the wizard with id: ' + this.id;
      }
    } else if (step == null) {
      return of(false);
    } else if (this.steps.get((<WizardStep>step).id) == null) {
      throw 'A step is being set that is not in the wizard';
    }

    //At this point we know that step isn't null and isn't a string. It must be a WizardStep.
    let newActiveStep: WizardStep = <WizardStep>step;
    let previousActiveStep: WizardStep = this.getActiveStep();

    let finishSettingActiveStep = () => {
      previousActiveStep.isActive = false;
      previousActiveStep.isComplete = true;
      newActiveStep.isActive = true;
      newActiveStep.isDisabled = false;

      this.activeStep = newActiveStep;
      this.fireChangeStream([previousActiveStep, newActiveStep]);
    };

    //We are going to run our functionality whether or not someone subscribes so we are using a replay subject here
    //which will emit when someone subscribes, and even if they subscribe late, they can still get the value.
    let subject: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    if (this.stepChangePrerequisite != null) {
      let prerequisiteResult: boolean | Observable<boolean> = this.stepChangePrerequisite(previousActiveStep, newActiveStep);

      if (typeof prerequisiteResult === 'boolean') {
        if (prerequisiteResult === true) {
          finishSettingActiveStep();
        } else {
          log('step change canceled');
        }
        subject.next(prerequisiteResult);
        subject.complete();
      } else {
        //prerequisiteResult may be the same object every time we call the stepChangePrerequisite function.
        //We need to unsubscribe when it fires so we don't continue to wait for it.
        prerequisiteResult.pipe(
          take(1) //This makes this subscription use the first firing of the observable and then automatically unsubscribe.
        ).subscribe((prerequisiteMet: boolean) => {

          if (prerequisiteMet) {
            finishSettingActiveStep();
          } else {
            log('step change canceled');
          }
          subject.next(prerequisiteMet);
          subject.complete();
        });
      }

    } else {
      finishSettingActiveStep();
      subject.next(true);
      subject.complete();
    }

    return subject;
  }

  getActiveStep(): WizardStep {
    if (this.activeStep == null) {
      if (this.steps.array.length > 0) {
        this.activeStep = this.steps.array[0].value;
      } else {
        throw 'Cannot get active wizard step because the wizard has no steps';
      }
    }

    return <WizardStep>this.activeStep;
  }

  nextStep(): Observable<boolean> {
    log('nextStep() called');

    let currentIndex: number = this.steps.indexOf(this.getActiveStep());
    if (currentIndex >= 0 && currentIndex + 1 < this.steps.length) {
      return this.setActiveStep(this.steps.getValueAt(currentIndex + 1));
    }

    return of(false);
  }

  previousStep(): Observable<boolean> {
    log('previousStep() called');

    let currentIndex: number = this.steps.indexOf(this.getActiveStep());
    if (currentIndex - 1 >= 0) {
      return this.setActiveStep(this.steps.getValueAt(currentIndex - 1));
    }

    return of(false);
  }
}
